import "./index.css";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import SSEProvider from "./providers/sseProvider.tsx";
import AudioProvider from "./providers/audioProvider.tsx";
import { NextUIProvider } from "@nextui-org/system";

import * as ReactDOM from "react-dom/client";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from "react-router-dom";
// Import routes for games and iframes
import { gameRoutes, iframeRoutes } from "./routes";
import Root from "./routes/root.tsx";
import Home from "./pages/Home/Home.tsx";
import ProtectedRoute from "./middleware/ProtectedRoute.tsx";

// Import static components
import NotFound from "./pages/General/404.tsx";
import { lazy } from "react";
import { ContactFormPage } from "./pages/ContactForm/index.tsx";
import Admin from "./pages/Admin/Admin.tsx";

const ProgramOverview = lazy(
  () => import("./pages/Profile/Referrals/ProgramOverview.tsx"),
);
const CasinoGameView = lazy(() => import("./pages/Games/Casino"));
const ResponsibleGambling = lazy(() => import("./pages/ResponsibleGambling"));
const PaymentstatusPage = lazy(
  () => import("./pages/Payment/PaymentStatus.tsx"),
);
const SelfExclusion = lazy(() => import("./pages/SelfExclusion/index.tsx"));
const GamblingWithRes = lazy(
  () => import("./pages/Maintenance/GamblingWithRes.tsx"),
);
const GlossaryPage = lazy(() => import("./pages/Glossary/glossary.tsx"));

const CoinMixingPolicy = lazy(
  () => import("./pages/Policies/coinMixingPolicies.tsx"),
);
const AMLPolicy = lazy(() => import("./pages/Policies/amlPolicies.tsx"));
const TermsOfServices = lazy(() => import("./pages/Policies/toc.tsx"));
const KYCPolicies = lazy(() => import("./pages/Policies/kyc.tsx"));

const BonusPage = lazy(() => import("@/pages/Bonus/BonusPage.tsx"));
const LoginBonusClaimHistoryPage = lazy(
  () => import("@/pages/Bonus/LoginBonusClaimHistoryPage.tsx"),
);
const DailyChallengeClaimHistoryPage = lazy(
  () => import("@/pages/Bonus/DailyChallengeClaimHistoryPage.tsx"),
);
const DailyChallengeLeaderboardPage = lazy(
  () => import("@/pages/Bonus/DailyChallengeLeaderboardPage.tsx"),
);
const ChatRoom = lazy(() => import("./components/dashboard/ChatRoom.tsx"));
const FAQPage = lazy(() => import("./pages/FAQ/faq.tsx"));
const LoginCallback = lazy(() => import("./pages/Login/LoginCallback.tsx"));
const RakebackSystem = lazy(
  () => import("./pages/Rakeback/RakebackSystem.tsx"),
);
const RakebackLeaderboard = lazy(
  () => import("./pages/Rakeback/RakebackLeaderboard.tsx"),
);
const HelpCenterPage = lazy(
  () => import("./pages/HelpCenter/HelpCenterPage.tsx"),
);
const GettingStarted = lazy(
  () => import("./pages/HelpCenter/GettingStarted.tsx"),
);
const Article = lazy(() => import("./pages/HelpCenter/Article.tsx"));
const Profile = lazy(() => import("./pages/Profile/Profile.tsx"));
const ElitePage = lazy(() => import("./pages/Elite/ElitePage.tsx"));
const PredictionGame = lazy(
  () => import("./pages/PredictionGame/PredictionGame.tsx"),
);
const LeaderBoard = lazy(
  () => import("./pages/PredictionGame/LeaderBoard/LeaderBoard.tsx"),
);
const PartnershipProgram = lazy(
  () => import("./pages/PartnershipProgram/PartnershipProgram.tsx"),
);
const Staking = lazy(() => import("./pages/Staking/Staking.tsx"));
const MainPage = lazy(() => import("./pages/Lobby/MainPage.tsx"));

const SharkDex = lazy(() => import("./pages/SharkDex/SharkDex.tsx"));

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/" element={<Root />}>
      {/* Public routes */}
      <Route index path="/" element={<Home />} />
      <Route path="/rakeback-system" element={<RakebackSystem />} />
      <Route path="/rakeback-leaderboard" element={<RakebackLeaderboard />} />
      <Route path="/help-center" element={<HelpCenterPage />} />
      <Route path="topic/:id" element={<GettingStarted />} />
      <Route path="topic/article/:id" element={<Article />} />
      <Route path="/maintenance" element={<GamblingWithRes />} />
      <Route path="/login-callback" element={<LoginCallback />} />
      <Route path="/self-exclusion" element={<SelfExclusion />} />
      <Route path="/kyc-policy" element={<KYCPolicies />} />
      <Route path="/responsible-gambling" element={<ResponsibleGambling />} />
      <Route path="/aml-policy" element={<AMLPolicy />} />
      <Route path="/coin-mixing-policy" element={<CoinMixingPolicy />} />
      <Route path="/toc" element={<TermsOfServices />} />
      <Route path="/responsible-gambling" element={<ResponsibleGambling />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/glossary" element={<GlossaryPage />} />
      <Route path="/partnership-program" element={<PartnershipProgram />} />
      <Route path="/payment/status" element={<PaymentstatusPage />} />
      <Route path="/support" element={<ContactFormPage />} />
      <Route
        path="/live-chat"
        element={
          <ChatRoom fullScreen={true} isOpen={true} handleClose={() => {}} />
        }
      />
      {/* <Route path="/partnership-program" element={<PartnershipProgram />} /> */}
      {/* <Route path="/api/v1/auth/:provider/callback" element={<AuthCallback />} /> */}

      {/* Protected user routes */}
      <Route element={<ProtectedRoute redirectPath="/404" />}>
        <Route path="/profile" element={<Profile />} />
        <Route path="/referrals-program" element={<ProgramOverview />} />
        <Route path="/elite" element={<ElitePage />} />
      </Route>

      {/* Admin routes */}
      <Route element={<ProtectedRoute redirectPath="/404" />}>
        <Route path="/admin" element={<Admin />} />
      </Route>

      {/* Games routes */}
      {/* {gameRoutes.map(({ path, component: Component }) => ( */}
      {/*   <Route key={path} path={path} element={<Component />} /> */}
      {/* ))} */}
      {/* Iframes */}
      {iframeRoutes.map(({ path, component: Component }) => (
        <Route key={path} path={path} element={<Component />} />
      ))}
      <Route path="/casino/lobby" element={<MainPage />} />
      <Route path="/casino/games/:name" element={<CasinoGameView />} />
      <Route path="/shark-dex" element={<SharkDex />} />
      <Route path="/prediction-game" element={<PredictionGame />} />
      <Route path="/prediction-game/leaderboard" element={<LeaderBoard />} />
      <Route path="/staking" element={<Staking />} />

      {/* Blogs routes */}
      {/* <Route path="/blogs" element={<BlogsList />} /> */}
      {/* <Route path="/blog" element={<BlogPage />} /> */}
      {/* <Route path="/blog/:id" element={<Post />} /> */}
      {/* <Route path="/blog/editBlog" element={<EditBlog />} /> */}
      {/* <Route path="/blog/blogPreview" element={<BlogPreview />} /> */}
      {/* <Route path="/blog/blogRemove" element={<BlogRemove />} /> */}

      {/* Bonus routes */}
      <Route path="/bonuses" element={<BonusPage />} />
      <Route
        path="/bonuses/claim-history"
        element={<LoginBonusClaimHistoryPage />}
      />
      <Route
        path="/bonuses/daily-challenges/claim-history"
        element={<DailyChallengeClaimHistoryPage />}
      />
      <Route
        path="/bonuses/daily-challenges/leaderboard"
        element={<DailyChallengeLeaderboardPage />}
      />

      {/* Fallback and defaults */}
      <Route path="/404" element={<NotFound />} />
      <Route path="*" element={<NotFound />} />
    </Route>,
  ),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <QueryClientProvider client={queryClient}>
    <NextUIProvider locale="en-GB">
      <SSEProvider>
        <AudioProvider>
          <RouterProvider router={router} />
        </AudioProvider>
      </SSEProvider>
    </NextUIProvider>
  </QueryClientProvider>,
);
